import { RCButton } from '@reverbdotcom/cadence/components';
import React from 'react';
import I18n from 'i18n-js';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import Location from '../lib/wrapped_location';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { CheckoutGuestAuthModal } from './CheckoutGuestAuthModal';
import { BuyItNowButtonFieldsFragment } from '@reverbdotcom/commons/src/gql/graphql';
import { isOwner } from '@reverbdotcom/commons/src/user_context_helpers';
import { BuyItNowPaymentOptions } from './BuyItNowPaymentOptions';
import { ButtonSize } from '@reverbdotcom/cadence/components/RCButton/styling';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';
import { gql } from '@reverbdotcom/commons/src/gql';

type Listing = BuyItNowButtonFieldsFragment;

interface Props {
  listing: Listing;
  quantity: number;
  size?: ButtonSize;
}

export function BuyItNowButton({
  listing,
  quantity,
  size = 'medium',
}: Props) {
  const user = useUser();
  const showGuestAuthModal = user.loggedOut;
  const [isPaymentMethodModalOpen, setPaymentMethodModalOpen] = React.useState(false);
  const [guestRedirectUrl, setGuestRedirectUrl] = React.useState(null);
  const isStandalonePaypal = listing.acceptedPaymentMethods.includes('paypal');

  function onButtonClick() {
    const customTrackedValues = {
      userId: user.id,
      isLocalPickupOnly: listing.shipping.localPickupOnly,
      isStandalonePaypal,
      isOutlet: listing.usOutlet,
      certified: !!listing.shop?.certifiedPreOwned,
    };
    const trackedExperiments = [];

    if (isStandalonePaypal) { trackedExperiments.push(experiments.STANDALONE_PAYPAL_BIN); }

    setPaymentMethodModalOpen(true);
    trackEvent({
      eventName: MParticleEventName.ClickedBuyItNowButton,
      listingId: listing.id,
      experiments: formatExperimentsForEventAttributes(user, trackedExperiments),
      ...customTrackedValues,
    });
  }

  function onStartCheckoutSuccess(redirectUrl: string) {
    if (showGuestAuthModal) {
      setPaymentMethodModalOpen(false);
      setGuestRedirectUrl(redirectUrl);
    } else {
      Location.assign(redirectUrl);
    }
  }

  return (
    <div className="buy-it-now-button__wrapper">
      <BuyItNowPaymentOptions
        listing={listing}
        quantity={quantity}
        modalOpen={isPaymentMethodModalOpen}
        onRequestClose={() => setPaymentMethodModalOpen(false)}
        onStartCheckoutSuccess={onStartCheckoutSuccess}
      />
      <RCButton
        theme="loud"
        variant="filled"
        onClick={onButtonClick}
        fullWidth
        disabled={isOwner(user, listing)}
        size={size}
      >
        {I18n.t('discovery.checkout.buyItNow.title')}
      </RCButton>
      <CheckoutGuestAuthModal
        active={!!guestRedirectUrl}
        redirectTo={guestRedirectUrl}
        trackSource="BuyItNowButton"
        onRequestClose={() => setGuestRedirectUrl(null)}
      />
    </div>
  );
}

export const BuyItNowButtonFragment = gql(`
  fragment BuyItNowButtonFields on Listing {
    _id
    id
    shipping {
      localPickupOnly
    }
    shop {
      ...Item2CPOShopData
    }
    usOutlet
    ...BuyItNowPaymentOptionsFields
  }
`);
